import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api, setToken, getToken } from "../api/client";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined); // undefined = loading, null = signed-out

  const refresh = useCallback(async () => {
    if (!getToken()) {
      setUser(null);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("Auth refresh failed:", e?.message);
      setToken(null);
      setUser(null);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
  }, []);

  const updateProfile = useCallback(async (patch) => {
    const { data } = await api.patch("/auth/me", patch);
    if (data.token) setToken(data.token); // email changed → new JWT
    setUser(data.user);
    return data.user;
  }, []);

  const changePassword = useCallback(async ({ current_password, new_password }) => {
    await api.post("/auth/change-password", { current_password, new_password });
  }, []);

  // Memoize so consumers don't re-render when AuthProvider re-renders for unrelated reasons.
  const value = useMemo(
    () => ({ user, login, logout, refresh, updateProfile, changePassword }),
    [user, login, logout, refresh, updateProfile, changePassword]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => useContext(AuthCtx);
